import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import BlogLayout from "./BlogLayout";
import {
  Ticket,
  UserPlus,
  Gift,
  Settings,
  Palette,
  Rocket,
  ArrowRight,
  Store,
  ShoppingBag,
  Calendar,
  Globe,
} from "lucide-react";

const HowToCreateKujiPage: React.FC = () => {
  const kujiTypes = [
    {
      name: "一番くじ風",
      description:
        "等級（A賞・B賞・ラストワン賞など）で景品を分ける定番スタイル。高額景品で話題性を作りやすい。",
      suitable: "カードショップ、アニメグッズ店、ECサイト",
    },
    {
      name: "スクラッチくじ",
      description:
        "デジタルスクラッチで当たりを削るエンタメ性の高いスタイル。手軽に参加できるため回転率が高い。",
      suitable: "飲食店、小売店、イベント会場",
    },
    {
      name: "ガラポンくじ",
      description:
        "ガラガラ抽選機をデジタル化。お祭り感を演出できるため集客イベントに最適。",
      suitable: "商店街イベント、地域の祭り、周年記念",
    },
  ];

  const comparisonData = [
    {
      method: "自社開発",
      cost: "100万円〜",
      period: "3〜6ヶ月",
      difficulty: "高",
      maintenance: "自社負担",
      flexibility: "◎ 自由度が高い",
    },
    {
      method: "SaaS（KUJI）",
      cost: "0円（初期費用無料）",
      period: "最短即日",
      difficulty: "低",
      maintenance: "不要",
      flexibility: "○ カスタマイズ可",
    },
    {
      method: "制作会社に委託",
      cost: "30〜100万円",
      period: "1〜3ヶ月",
      difficulty: "中",
      maintenance: "別途契約",
      flexibility: "△ 追加費用が発生",
    },
  ];

  const steps = [
    {
      number: "01",
      title: "無料アカウントを作成",
      icon: <UserPlus className="w-8 h-8" />,
      description:
        "メールアドレスとパスワードだけで登録完了。クレジットカード不要で、すぐにダッシュボードにアクセスできます。",
    },
    {
      number: "02",
      title: "くじの景品を登録",
      icon: <Gift className="w-8 h-8" />,
      description:
        "景品の名前・画像・説明を登録します。A賞・B賞・C賞のような等級分けも簡単に設定できます。",
    },
    {
      number: "03",
      title: "当選確率・枚数を設定",
      icon: <Settings className="w-8 h-8" />,
      description:
        "各景品の当選確率と用意する枚数を設定。在庫がなくなると自動で売り切れ表示になるため、管理の手間がかかりません。",
    },
    {
      number: "04",
      title: "デザイン・公開設定",
      icon: <Palette className="w-8 h-8" />,
      description:
        "くじのタイトル、説明文、価格を設定。お店のブランドに合わせたカスタマイズも可能です。",
    },
    {
      number: "05",
      title: "公開・販売開始",
      icon: <Rocket className="w-8 h-8" />,
      description:
        "設定が完了したら公開ボタンを押すだけ。SNSやLINEでくじのURLをシェアして集客を始めましょう。",
    },
  ];

  const useCases = [
    {
      icon: <Store className="w-6 h-6" />,
      title: "飲食店",
      description:
        "来店促進キャンペーンとして活用。「お食事券」「ドリンク無料券」などを景品に設定し、リピート来店を促進。",
    },
    {
      icon: <ShoppingBag className="w-6 h-6" />,
      title: "小売店・ECサイト",
      description:
        "商品の販促や在庫処分に。「限定グッズ」「割引クーポン」を景品にして、売上アップと在庫回転を実現。",
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "イベント・お祭り",
      description:
        "周年記念やセールイベントの集客に。ガラポン風くじで参加者を楽しませながら、イベントの盛り上がりを演出。",
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "オンラインビジネス",
      description:
        "SNSフォロワー向けのキャンペーンや、会員限定特典として活用。物理的な場所を問わず全国からくじに参加可能。",
    },
  ];

  return (
    <BlogLayout
      title="一番くじの作り方｜オンラインくじを無料で作成する方法"
      description="一番くじ風のオンラインくじを無料で作る方法を5ステップで解説。飲食店・小売店・イベントでのくじ販売の始め方がわかります。"
      category="ガイド"
      publishedAt="2026年3月9日"
      breadcrumbs={[{ label: "一番くじの作り方" }]}
    >
      <Helmet>
        <title>
          一番くじの作り方｜オンラインくじを無料で作成する方法 | JoyBase
        </title>
        <meta
          name="description"
          content="一番くじ風のオンラインくじを無料で作る方法を解説。スクラッチ・ガラポンにも対応。初期費用0円のKUJIなら5ステップで簡単にくじ販売を始められます。"
        />
        <meta
          name="keywords"
          content="一番くじ 作り方, オンラインくじ 作成, くじ 作り方, スクラッチくじ 作成, ガラポン 作り方, くじ販売 始め方, くじ 無料"
        />
        <meta
          property="og:title"
          content="一番くじの作り方｜オンラインくじを無料で作成する方法 | JoyBase"
        />
        <meta
          property="og:description"
          content="一番くじ風のオンラインくじを無料で作る方法を解説。スクラッチ・ガラポンにも対応。初期費用0円のKUJIなら5ステップで簡単にくじ販売を始められます。"
        />
        <meta
          property="og:url"
          content="https://joybase.jp/blog/how-to-create-kuji"
        />
        <meta property="og:type" content="article" />
        <link
          rel="canonical"
          href="https://joybase.jp/blog/how-to-create-kuji"
        />
      </Helmet>

      {/* はじめに */}
      <section className="mb-12">
        <h2>はじめに：なぜ今「オンラインくじ」が注目されているのか</h2>
        <p>
          「一番くじ」に代表されるくじ販売は、もはやコンビニやアニメショップだけのものではありません。
          飲食店の来店促進、小売店のセール企画、ECサイトの販促キャンペーンなど、
          <strong>業種を問わずさまざまなビジネスで「くじ」を活用する動き</strong>
          が広がっています。
        </p>
        <p>
          その背景には、オンラインくじの普及があります。物理的なくじ箱やスクラッチカードを用意する必要がなく、
          <strong>
            スマートフォンひとつで全国どこからでも参加できる手軽さ
          </strong>
          が、店舗側にも顧客側にも大きなメリットをもたらしています。
        </p>
        <p>
          この記事では、一番くじ風のオンラインくじを
          <strong>初期費用0円で作成する方法</strong>
          を、5つのステップでわかりやすく解説します。
        </p>
      </section>

      {/* 一番くじの種類と特徴 */}
      <section className="mb-12">
        <h2>一番くじの種類と特徴</h2>
        <p>
          オンラインくじにはいくつかの種類があります。それぞれの特徴を理解して、あなたのビジネスに合ったスタイルを選びましょう。
        </p>

        <div className="space-y-4 not-prose mt-6">
          {kujiTypes.map((type, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <Ticket className="w-6 h-6 text-yellow-500 mt-1" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    {type.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-2">
                    {type.description}
                  </p>
                  <p className="text-xs text-gray-500">
                    <span className="font-semibold">おすすめの用途：</span>
                    {type.suitable}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* オンラインくじを作る3つの方法 */}
      <section className="mb-12">
        <h2>オンラインくじを作る3つの方法</h2>
        <p>
          オンラインくじを作る方法は大きく3つあります。それぞれの費用・期間・難易度を比較してみましょう。
        </p>

        <div className="not-prose mt-6 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="bg-gray-100 border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">
                  比較項目
                </th>
                {comparisonData.map((item, index) => (
                  <th
                    key={index}
                    className={`border border-gray-300 px-4 py-3 text-left font-semibold ${
                      item.method === "SaaS（KUJI）"
                        ? "bg-yellow-50 text-yellow-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {item.method}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 px-4 py-3 font-semibold text-gray-800 bg-gray-50">
                  初期費用
                </td>
                {comparisonData.map((item, index) => (
                  <td
                    key={index}
                    className={`border border-gray-300 px-4 py-3 ${
                      item.method === "SaaS（KUJI）"
                        ? "bg-yellow-50 font-bold text-yellow-700"
                        : "text-gray-700"
                    }`}
                  >
                    {item.cost}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-3 font-semibold text-gray-800 bg-gray-50">
                  構築期間
                </td>
                {comparisonData.map((item, index) => (
                  <td
                    key={index}
                    className={`border border-gray-300 px-4 py-3 ${
                      item.method === "SaaS（KUJI）"
                        ? "bg-yellow-50 font-bold text-yellow-700"
                        : "text-gray-700"
                    }`}
                  >
                    {item.period}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-3 font-semibold text-gray-800 bg-gray-50">
                  難易度
                </td>
                {comparisonData.map((item, index) => (
                  <td
                    key={index}
                    className={`border border-gray-300 px-4 py-3 ${
                      item.method === "SaaS（KUJI）"
                        ? "bg-yellow-50 font-bold text-yellow-700"
                        : "text-gray-700"
                    }`}
                  >
                    {item.difficulty}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-3 font-semibold text-gray-800 bg-gray-50">
                  保守・運用
                </td>
                {comparisonData.map((item, index) => (
                  <td
                    key={index}
                    className={`border border-gray-300 px-4 py-3 ${
                      item.method === "SaaS（KUJI）"
                        ? "bg-yellow-50 font-bold text-yellow-700"
                        : "text-gray-700"
                    }`}
                  >
                    {item.maintenance}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-3 font-semibold text-gray-800 bg-gray-50">
                  カスタマイズ
                </td>
                {comparisonData.map((item, index) => (
                  <td
                    key={index}
                    className={`border border-gray-300 px-4 py-3 ${
                      item.method === "SaaS（KUJI）"
                        ? "bg-yellow-50"
                        : "text-gray-700"
                    }`}
                  >
                    {item.flexibility}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <p className="mt-4">
          <strong>コストを抑えてすぐに始めたい場合は、SaaS型のくじ作成サービスがおすすめ</strong>
          です。なかでも
          <Link to="/kuji" className="text-blue-600 hover:text-blue-800">
            KUJI by JoyBase
          </Link>
          なら、初期費用・月額費用0円で一番くじ風のオンラインくじを作成できます。
        </p>
      </section>

      {/* KUJI を使ったくじの作り方（5ステップ） */}
      <section className="mb-12">
        <h2>KUJI を使ったくじの作り方（5ステップ）</h2>
        <p>
          <Link to="/kuji" className="text-blue-600 hover:text-blue-800">
            KUJI
          </Link>
          を使えば、プログラミングの知識がなくてもオンラインくじを簡単に作成できます。以下の5ステップで進めましょう。
        </p>

        <div className="space-y-6 not-prose mt-8">
          {steps.map((step, index) => (
            <div
              key={index}
              className="relative bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
            >
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-yellow-400 text-black font-bold text-lg rounded-full">
                      {step.number}
                    </div>
                    <div className="flex items-center justify-center w-12 h-12 bg-yellow-50 text-yellow-600 rounded-full">
                      {step.icon}
                    </div>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="not-prose mt-8 bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <p className="text-gray-800 font-semibold mb-3">
            最短即日でくじ販売を始められます
          </p>
          <Link
            to="/kuji"
            className="inline-flex items-center justify-center px-6 py-3 bg-yellow-400 text-gray-900 font-bold rounded-lg hover:bg-yellow-500 transition-colors"
          >
            KUJI の詳細を見る
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </section>

      {/* 活用シーン */}
      <section className="mb-12">
        <h2>くじの活用シーン</h2>
        <p>
          オンラインくじはさまざまな業種・シーンで活用されています。あなたのビジネスに合った使い方を見つけましょう。
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose mt-6">
          {useCases.map((useCase, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-yellow-100 text-yellow-600 rounded-lg flex items-center justify-center">
                  {useCase.icon}
                </div>
                <h3 className="font-bold text-gray-900">{useCase.title}</h3>
              </div>
              <p className="text-sm text-gray-600">{useCase.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* まとめ */}
      <section className="mb-12">
        <h2>まとめ</h2>
        <p>
          一番くじ風のオンラインくじは、
          <strong>
            特別な技術知識がなくても、SaaS型のくじ作成サービスを使えば簡単に始められます
          </strong>
          。
        </p>
        <p>この記事のポイントをおさらいしましょう。</p>
        <ul>
          <li>
            オンラインくじには一番くじ風・スクラッチ・ガラポンなど複数のスタイルがある
          </li>
          <li>
            SaaS（KUJI）なら初期費用0円・最短即日でくじ販売を開始できる
          </li>
          <li>5ステップの簡単な設定でプロ品質のくじが完成</li>
          <li>
            景品表示法や特商法の注意点を押さえれば、安心して運営できる
          </li>
          <li>
            飲食店・小売店・イベント・ECなど、幅広い業種で活用可能
          </li>
        </ul>

        <div className="not-prose mt-8 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-xl p-6 md:p-8 text-center">
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
            KUJI でオンラインくじを無料で始めよう
          </h3>
          <p className="text-gray-800 text-sm md:text-base mb-4">
            初期費用・月額費用0円。売上が発生するまで一切費用はかかりません。
          </p>
          <Link
            to="/kuji"
            className="inline-flex items-center justify-center px-8 py-3 bg-gray-900 text-white font-bold rounded-lg hover:bg-gray-800 transition-colors"
          >
            KUJI の詳細を見る
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </section>

      {/* 関連記事 */}
      <section>
        <h2>関連記事</h2>
        <div className="not-prose space-y-3">
          <Link
            to="/blog/how-to-create-oripa-site"
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
          >
            <ArrowRight className="w-4 h-4" />
            オリパの作り方｜サイト構築3つの方法を徹底比較
          </Link>
          <Link
            to="/blog/how-to-start"
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
          >
            <ArrowRight className="w-4 h-4" />
            JoyBaseでオリパサイトを始める方法
          </Link>
          <Link
            to="/blog/oripa-regulation-guide"
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
          >
            <ArrowRight className="w-4 h-4" />
            オリパ運営の法的注意点ガイド
          </Link>
        </div>
      </section>
    </BlogLayout>
  );
};

export default HowToCreateKujiPage;
